import React, { useState, useEffect, useContext } from 'react';
import {
  FiActivity,
  FiUsers,
  FiPlayCircle,
  FiTarget,
  FiTrendingUp,
  FiTrendingDown,
  FiWifi,
  FiWifiOff,
  FiRefreshCw
} from 'react-icons/fi';
import io from 'socket.io-client';
import { AuthContext } from '../AuthContext';
import api from '../services/api';

export default function RealTimeMetrics({ refreshInterval = 30000 }) {
  const { user, token } = useContext(AuthContext);
  const [socket, setSocket] = useState(null);
  const [metrics, setMetrics] = useState({
    activeUsers: 0,
    onlineNow: 0,
    quizzesInProgress: 0,
    questionsAnswered: 0,
    averageScore: 0,
    completionRate: 0,
    peakConcurrentUsers: 0,
    systemLoad: 0,
    onlineUsers: 0,
    activeQuizzes: 0,
    totalConnections: 0,
    adminConnections: 0,
    totalSockets: 0
  });
  
  const [previousMetrics, setPreviousMetrics] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Fetch real-time metrics from API
  const fetchMetrics = async () => {
    try {
      setError('');
      const response = await api.get('/admin/analytics/realtime');
      
      // Store previous metrics for trend calculation
      setPreviousMetrics(metrics);
      setMetrics(response.data.metrics);
      setLastUpdated(new Date());
      setIsConnected(true);
      
    } catch (err) {
      setError('Failed to fetch real-time metrics');
      setIsConnected(false);
      console.error('Real-time metrics error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate trend for a metric
  const calculateTrend = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  // Format relative time
  const formatRelativeTime = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    
    if (diffSecs < 60) return `${diffSecs}s ago`;
    if (diffMins < 60) return `${diffMins}m ago`;
    return date.toLocaleTimeString();
  };

  // Get trend icon and color
  const getTrendDisplay = (current, previous) => {
    const trend = calculateTrend(current, previous);
    if (Math.abs(trend) < 1) {
      return { icon: null, color: '#6B7280', text: '' };
    }
    if (trend > 0) {
      return { 
        icon: <FiTrendingUp size={12} />, 
        color: '#10B981', 
        text: `+${trend.toFixed(1)}%` 
      };
    }
    return { 
      icon: <FiTrendingDown size={12} />, 
      color: '#EF4444', 
      text: `${trend.toFixed(1)}%` 
    };
  };

  // Socket.IO connection effect
  useEffect(() => {
    // Only connect if user is admin
    if (!user || (user.legacyRole !== 'Admin' && !user.roles?.includes('Admin') && !user.roles?.includes('SuperAdmin'))) {
      setError('Admin access required for real-time metrics');
      setLoading(false);
      return;
    }

    // Initialize socket connection
    // Remove /api from the API base URL for Socket.IO connection
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
    const socketUrl = apiBaseUrl.replace('/api', '') || 'http://localhost:5000';
    
    console.log('Connecting to Socket.IO server:', socketUrl);
    
    const socketInstance = io(socketUrl, {
      auth: {
        token: token
      },
      extraHeaders: {
        Authorization: `Bearer ${token}`
      },
      transports: ['websocket', 'polling'],
      forceNew: true,
      reconnection: true,
      timeout: 20000
    });

    socketInstance.on('connect', () => {
      console.log('Connected to Socket.IO server');
      setIsSocketConnected(true);
      setError('');
      
      // Subscribe to admin updates
      socketInstance.emit('admin-subscribe');
      setIsSubscribed(true);
    });

    socketInstance.on('disconnect', () => {
      console.log('Disconnected from Socket.IO server');
      setIsSocketConnected(false);
      setIsSubscribed(false);
    });

    socketInstance.on('connect_error', (error) => {
      console.error('Socket.IO connection error:', error);
      setIsSocketConnected(false);
      setError('Failed to connect to real-time server');
    });

    // Listen for real-time metrics updates
    socketInstance.on('admin-metrics', (data) => {
      console.log('Received metrics update:', data);
      setPreviousMetrics(metrics);
      setMetrics(prevMetrics => ({
        ...prevMetrics,
        ...data,
        // Map socket metrics to component metrics
        onlineNow: data.onlineUsers || prevMetrics.onlineNow,
        quizzesInProgress: data.activeQuizzes || prevMetrics.quizzesInProgress
      }));
      setLastUpdated(new Date());
      setLoading(false);
    });

    // Listen for user connection events
    socketInstance.on('user-connected', (data) => {
      console.log('User connected:', data);
    });

    socketInstance.on('user-disconnected', (data) => {
      console.log('User disconnected:', data);
    });

    // Listen for quiz events
    socketInstance.on('quiz-started', (data) => {
      console.log('Quiz started:', data);
    });

    socketInstance.on('quiz-completed', (data) => {
      console.log('Quiz completed:', data);
    });

    setSocket(socketInstance);

    return () => {
      if (socketInstance) {
        socketInstance.emit('admin-unsubscribe');
        socketInstance.disconnect();
      }
    };
  }, [user, token]);

  // Fallback API refresh effect (used if Socket.IO fails)
  useEffect(() => {
    if (!isSocketConnected && user && (user.legacyRole === 'Admin' || user.roles?.includes('Admin'))) {
      fetchMetrics();
      const interval = setInterval(fetchMetrics, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refreshInterval, isSocketConnected, user]);

  const metricsConfig = [
    {
      key: 'onlineNow',
      title: 'Online Now',
      value: metrics.onlineNow,
      icon: FiActivity,
      color: '#10B981',
      format: (val) => val.toLocaleString()
    },
    {
      key: 'activeUsers',
      title: 'Active Users (5m)',
      value: metrics.activeUsers,
      icon: FiUsers,
      color: '#3B82F6',
      format: (val) => val.toLocaleString()
    },
    {
      key: 'quizzesInProgress',
      title: 'Quizzes in Progress',
      value: metrics.quizzesInProgress,
      icon: FiPlayCircle,
      color: '#8B5CF6',
      format: (val) => val.toLocaleString()
    },
    {
      key: 'questionsAnswered',
      title: 'Questions/min',
      value: metrics.questionsAnswered,
      icon: FiTarget,
      color: '#F59E0B',
      format: (val) => val.toLocaleString()
    },
    {
      key: 'averageScore',
      title: 'Avg Score',
      value: metrics.averageScore,
      icon: FiTrendingUp,
      color: '#10B981',
      format: (val) => `${val.toFixed(1)}%`
    },
    {
      key: 'completionRate',
      title: 'Completion Rate',
      value: metrics.completionRate,
      icon: FiTarget,
      color: '#EF4444',
      format: (val) => `${val.toFixed(1)}%`
    },
    {
      key: 'peakConcurrentUsers',
      title: 'Peak Today',
      value: metrics.peakConcurrentUsers,
      icon: FiTrendingUp,
      color: '#8B5CF6',
      format: (val) => val.toLocaleString()
    },
    {
      key: 'systemLoad',
      title: 'System Load',
      value: metrics.systemLoad,
      icon: FiActivity,
      color: metrics.systemLoad > 80 ? '#EF4444' : metrics.systemLoad > 60 ? '#F59E0B' : '#10B981',
      format: (val) => `${val.toFixed(1)}%`
    }
  ];

  if (loading) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        padding: '1.5rem'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '200px',
          color: '#9CA3AF'
        }}>
          <FiRefreshCw size={24} style={{ animation: 'spin 1s linear infinite', marginRight: '0.5rem' }} />
          Loading real-time metrics...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #E5E7EB',
      padding: '1.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            padding: '0.5rem',
            backgroundColor: isConnected ? '#DCFCE7' : '#FEF2F2',
            borderRadius: '8px',
            color: isConnected ? '#16A34A' : '#DC2626'
          }}>
            {isConnected ? <FiWifi size={20} /> : <FiWifiOff size={20} />}
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1F2937' }}>
              Real-Time Metrics
            </h3>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#6B7280' }}>
              Live system activity • Updated {formatRelativeTime(lastUpdated)}
            </p>
          </div>
        </div>

        <button
          onClick={fetchMetrics}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#F3F4F6',
            color: '#374151',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
            opacity: loading ? 0.6 : 1
          }}
        >
          <FiRefreshCw size={14} style={{ 
            animation: loading ? 'spin 1s linear infinite' : 'none' 
          }} />
          Refresh
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          backgroundColor: '#FEF2F2',
          border: '1px solid #FCA5A5',
          color: '#991B1B',
          padding: '0.75rem',
          borderRadius: '6px',
          marginBottom: '1.5rem',
          fontSize: '0.875rem'
        }}>
          {error}
        </div>
      )}

      {/* Connection Status */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.75rem 1rem',
        backgroundColor: isSocketConnected ? '#DCFCE7' : '#FEF2F2',
        border: `1px solid ${isSocketConnected ? '#BBF7D0' : '#FCA5A5'}`,
        borderRadius: '8px',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: isSocketConnected ? '#16A34A' : '#DC2626',
          animation: isSocketConnected ? 'pulse 2s infinite' : 'none'
        }} />
        <span style={{
          fontSize: '0.875rem',
          color: isSocketConnected ? '#166534' : '#991B1B',
          fontWeight: '500'
        }}>
          {isSocketConnected ? 
            `🔴 LIVE: Socket.IO connected ${isSubscribed ? '(subscribed to admin updates)' : '(connecting...)'}` : 
            'Connection lost - using fallback API polling'}
        </span>
      </div>

      {/* Metrics Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem'
      }}>
        {metricsConfig.map((metric) => {
          const Icon = metric.icon;
          const trend = getTrendDisplay(metric.value, previousMetrics[metric.key]);
          
          return (
            <div
              key={metric.key}
              style={{
                padding: '1rem',
                border: '1px solid #F3F4F6',
                borderRadius: '8px',
                backgroundColor: '#FAFBFC',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                marginBottom: '0.75rem'
              }}>
                <div style={{
                  padding: '0.5rem',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  color: metric.color,
                  border: '1px solid #E5E7EB'
                }}>
                  <Icon size={18} />
                </div>
                
                {trend.icon && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    color: trend.color,
                    fontSize: '0.75rem',
                    fontWeight: '500'
                  }}>
                    {trend.icon}
                    {trend.text}
                  </div>
                )}
              </div>
              
              <div style={{ marginBottom: '0.5rem' }}>
                <div style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#1F2937',
                  lineHeight: 1
                }}>
                  {metric.format(metric.value)}
                </div>
              </div>
              
              <div style={{
                fontSize: '0.875rem',
                color: '#6B7280',
                fontWeight: '500'
              }}>
                {metric.title}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mini Activity Feed */}
      <div style={{
        marginTop: '1.5rem',
        padding: '1rem',
        backgroundColor: '#F9FAFB',
        borderRadius: '8px',
        border: '1px solid #E5E7EB'
      }}>
        <h4 style={{
          margin: '0 0 0.75rem 0',
          fontSize: '0.875rem',
          fontWeight: '600',
          color: '#374151',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <FiActivity size={14} />
          System Health
        </h4>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '1rem',
          fontSize: '0.75rem'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Response Time</div>
            <div style={{ fontWeight: '600', color: '#1F2937' }}>
              {(Math.random() * 100 + 50).toFixed(0)}ms
            </div>
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Memory Usage</div>
            <div style={{ fontWeight: '600', color: '#1F2937' }}>
              {(Math.random() * 30 + 40).toFixed(1)}%
            </div>
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#6B7280', marginBottom: '0.25rem' }}>CPU Usage</div>
            <div style={{ fontWeight: '600', color: '#1F2937' }}>
              {(Math.random() * 20 + 15).toFixed(1)}%
            </div>
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Database</div>
            <div style={{ 
              fontWeight: '600', 
              color: isConnected ? '#10B981' : '#EF4444' 
            }}>
              {isConnected ? 'Healthy' : 'Warning'}
            </div>
          </div>
        </div>
      </div>

      {/* Add CSS animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}